import { getConfig } from "../config/config";
import * as xlsx from "xlsx"
import * as FileSaver from "file-saver";
import { getCountryName } from './countryName';
import ExcelJS from 'exceljs';
import { validateGtin } from "../helpers/validateGetin";
import { formatAdditionalTradeItemIdentificationsList } from "../helpers/additionalTradeItemIdentification";

export function exportFileTestCSV(dataPrepared: any[], filetype: string) {
  const hasCodigoErro = dataPrepared.some(item => item['Código de erro'] && item['Código de erro'].toString().trim() !== "");
  const hasErro = dataPrepared.some(item => item['Erro'] && item['Erro'].toString().trim() !== "");

  dataPrepared.forEach(item => {
    if (!hasCodigoErro) delete item['Código de erro'];
    if (!hasErro) delete item['Erro'];
  });

  const ws = xlsx.utils.json_to_sheet(dataPrepared);
  const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
  const bookType = (filetype === 'txt' || filetype === 'csv') ? 'csv' : 'csv';
  const excelBuffer = xlsx.write(wb, { bookType: bookType as xlsx.BookType, type: "array" });
  const data = new Blob([excelBuffer]);
  FileSaver.saveAs(data, "arquivo." + filetype);
}

  const getFriendlyUnit = (unitCode: string | undefined | null) => {
    const units: Record<string, string> = {
      'CMT': 'cm',
      'MTR': 'm',
      'KGM': 'kg',
      'GRM': 'g',
      'MLT': 'ml',
      'LTR': 'l'
    };

    if (!unitCode) return "";
    
    return units[unitCode] || unitCode;
  };

export async function exportFileTest(dataPrepared: any[], filetype: string) {
  if (filetype !== 'xls') {
    exportFileTestCSV(dataPrepared, filetype);
    return;
  }

  if (dataPrepared.length === 0) {
    return;
  }

  const hasCodigoErro = dataPrepared.some(item => item['Código de erro'] && item['Código de erro'].toString().trim() !== "");
  const hasErro = dataPrepared.some(item => item['Erro'] && item['Erro'].toString().trim() !== "");

  dataPrepared.forEach(item => {
    if (!hasCodigoErro) delete item['Código de erro'];
    if (!hasErro) delete item['Erro'];
  });

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Análise Detalhada');

  const headerFontColor = '002060';
  const titleColor = '002060';

  const startColumn = 2; 
  worksheet.getColumn(1).width = 3; 
  worksheet.getColumn(2).width = 14; 
  worksheet.getColumn(3).width = 18; 

  worksheet.getRow(1).height = 35;
  worksheet.getRow(2).height = 50;
  worksheet.getRow(3).height = 40;

  const logoUrl = `${window.location.origin}/logoxls.png`;
  
  try {
    const response = await fetch(logoUrl);
    const blob = await response.blob();
    const reader = new FileReader();
    
    await new Promise<void>((resolve, reject) => {
      reader.onload = () => resolve();
      reader.onerror = reject;
      reader.readAsArrayBuffer(blob);
    });

    const imageBuffer = reader.result as ArrayBuffer;
    const imageId = workbook.addImage({
      buffer: imageBuffer,
      extension: 'png',
    });

    worksheet.addImage(imageId, 'B1:C2');
  } catch (error) {
    console.warn('Não foi possível carregar a imagem do logo:', error);
  }

  worksheet.mergeCells('D1:E2');
  const titleCell = worksheet.getCell('D1');
  titleCell.font = {
    name: 'Verdana',
    size: 36,
    bold: true,
    color: { argb: titleColor }
  };
  titleCell.alignment = { vertical: 'middle', horizontal: 'left' };

  const headerRowNumber = 4;

  const columns = Object.keys(dataPrepared[0]);
  
  const columnWidths: number[] = columns.map((header, index) => {
    let maxWidth = header.length;
    
    dataPrepared.forEach((row: any) => {
      const value = row[header];
      if (value !== null && value !== undefined) {
        const cellLength = String(value).length;
        if (cellLength > maxWidth) {
          maxWidth = cellLength;
        }
      }
    });
    
    return Math.min(Math.max(maxWidth + 2, 10), 50);
  });
  columns.forEach((col, index) => {
    const column = worksheet.getColumn(startColumn + index);
    const colIndex = startColumn + index; 
    
    if (colIndex === 2 || colIndex === 3) {
      column.width = Math.max(columnWidths[index], 14);
    } 
    else if (colIndex === 4 || colIndex === 5) {
      column.width = Math.max(columnWidths[index], 40);
    } 
    else {
      column.width = columnWidths[index];
    }
    column.key = col;
  });

  const whiteBorder = {
    top: { style: 'thin' as const, color: { argb: 'FFFFFFFF' } },
    left: { style: 'thin' as const, color: { argb: 'FFFFFFFF' } },
    bottom: { style: 'thin' as const, color: { argb: 'FFFFFFFF' } },
    right: { style: 'thin' as const, color: { argb: 'FFFFFFFF' } }
  };

  const headerRow = worksheet.getRow(headerRowNumber);
  columns.forEach((col, index) => {
    const cell = headerRow.getCell(startColumn + index);
    cell.value = col;
    cell.font = {
      name: 'Calibri',
      size: 11,
      bold: true,
      color: { argb: headerFontColor }
    };
    cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
    cell.border = whiteBorder;
  });
  headerRow.height = 20;

  dataPrepared.forEach((rowData: any, rowIndex: number) => {
    const row = worksheet.getRow(headerRowNumber + 1 + rowIndex);
    
    const sincronizadoValue = rowData['Sincronizado CCG'];
    const shouldHighlight = sincronizadoValue && 
      (sincronizadoValue.toString().toUpperCase() === 'SIM');
    
    columns.forEach((col, colIndex) => {
      const cell = row.getCell(startColumn + colIndex);
      cell.value = rowData[col];
      cell.font = { name: 'Calibri', size: 11 };
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      cell.border = whiteBorder;
      
      if (shouldHighlight) {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'A9D08E' }
        };
      }
    });
  });

  const lastColumn = startColumn + columns.length - 1;
  const lastRow = headerRowNumber + dataPrepared.length;
  
  worksheet.autoFilter = {
    from: { row: headerRowNumber, column: startColumn },
    to: { row: lastRow, column: lastColumn }
  };

  worksheet.views = [
    { state: 'frozen', ySplit: headerRowNumber, activeCell: 'B5', showGridLines: false }
  ];

  const buffer = await workbook.xlsx.writeBuffer();
  const data = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  FileSaver.saveAs(data, 'analise_detalhada.xlsx');
}

function merge(obj1: any, obj2: any) {
  let merged = {
    ...obj1,
    ...obj2
  };
  Object.keys(obj1).filter(k => obj2.hasOwnProperty(k)).forEach((k) => {
    merged[k] = obj1[k] + "," + obj2[k]
  })
  return merged
}

export function exportFileFrontEndGTIN(data: any) {
  let c_GTIN = data.GTIN || "";
  let c_Marca = "";
  var linkset = {}
  var linkGroup = {}
  var contGroup = 0
  var grupos = {}

  if (data.VBG) {
    if (data.VBG.linkset) {
      Object.keys(data.VBG.linkset).map((key, value) => {    
        if (Array.isArray(data.VBG.linkset[key])) {
          var links = {}
          contGroup++
          var nomeGrupo = "GRUPO_LINK" + contGroup + "_NOME"
          var descricaoGrupo = "GRUPO_LINK" + contGroup + "_DESCRICAO"
          var contLinks = 0
          var grupo = {}

          Object.values(data.VBG.linkset[key]).map((item: any, index) => {                            
            if ('href' in item) {
              contLinks++
              var linkHref = "GRUPO_LINK" + contGroup + "_LINK" + contLinks + "_HREF"
              var linkTitle = "GRUPO_LINK" + contGroup + "_LINK" + contLinks + "_TITLE"
              var linkType = "GRUPO_LINK" + contGroup + "_LINK" + contLinks + "_TYPE"
              var linkLanguage = "GRUPO_LINK" + contGroup + "_LINK" + contLinks + "_LANGUAGE"
              var link = {
                [linkHref]: item.href || '',
                [linkTitle]: item.title || '',
                [linkType]: item.type || '',
                [linkLanguage]: item.hreflang && item.hreflang.length > 0 ? item.hreflang[0] : ''            
              }
              links = merge(links, link)
            } else {
              grupo = {
                [nomeGrupo]: data.VBG.linkset[key][index].name,
                [descricaoGrupo]: data.VBG.linkset[key][index].definition
              }
            }
          })
          var grupoFinal = merge(grupo, links)
          grupos = merge(grupos, grupoFinal)
        }
      })
    }
  }

  if (data?.CNP?.product?.brandNameInformationLang?.length > 0) {
    data.CNP.product.brandNameInformationLang.map((item: any) => {
      c_Marca += `(${item.languageCode}) ${item.brandName} `;
    });
  } else if (data?.VBG?.brandName?.length > 0) {
    data.VBG.brandName.map((item: any) => {
      c_Marca += `(${item.language}) ${item.value} `;
    });
  }

  let c_Descricao = "";
  if (data?.CNP?.product?.tradeItemDescriptionInformationLang?.length > 0) {
    data.CNP.product.tradeItemDescriptionInformationLang.map((item: any) => {
      c_Descricao += `(${item.languageCode}) ${item.tradeItemDescription} `;
    });
  } else if (data?.VBG?.productDescription?.length > 0) {
    data.VBG.productDescription.map((item: any) => {
      c_Descricao += `(${item.language}) ${item.value} `;
    });
  }

  let c_URLImagem = "";
  if (data?.CNP?.product?.referencedFileInformations?.length > 0) {
    data.CNP.product.referencedFileInformations.map((item: any) => {
      c_URLImagem += `(${item.languageCode}) ${item.uniformResourceIdentifier} `;
    });
  } else if (data?.VBG?.productImageUrl?.length > 0) {
    data.VBG.productImageUrl.map((item: any) => {
      c_URLImagem += `(${item.language}) ${item.value} `;
    });
  }

  let c_ClassificacaoGlobal = "";
  if (!!data.CNP && !!data.CNP.product) {
    c_ClassificacaoGlobal = data.CNP.product.tradeItemClassification.gpcCategoryCode || "";
  }
  if (!c_ClassificacaoGlobal && !!data.VBG) {
    c_ClassificacaoGlobal = data.VBG.gpcCategoryCode || "";
  }

  let c_ClassificacaoGlobalNome = "";
  if (!!data.VBG && !!data.VBG.gpcCategoryName) {
    c_ClassificacaoGlobalNome = data.VBG.gpcCategoryName || "";
  }

  if (!!data.CNP && !!data.CNP.product && !!data.CNP.product.tradeItemClassification && !!data.CNP.product.tradeItemClassification.gpcCategoryName) {
    c_ClassificacaoGlobalNome = data.CNP.product.tradeItemClassification.gpcCategoryName || "";
  }

  let c_ConteudoLiquido = "";
  if (data?.CNP?.product?.tradeItemMeasurements?.netContent) {
    c_ConteudoLiquido = data.CNP.product.tradeItemMeasurements.netContent.originalValue + " " + data.CNP.product.tradeItemMeasurements.netContent.measurementUnitCode;
  } else if (data?.VBG?.netContent?.[0]) {
    c_ConteudoLiquido = data.VBG.netContent[0].value + " " + data.VBG.netContent[0].unitCode;
  }

  let countriesComplete: { sigla: any; name: string; }[] = []
  if (data?.VBG?.productImageUrl?.length > 0 && data?.VBG?.countryOfSaleCode) {
    data.VBG.countryOfSaleCode.map((item: any) => {
      const nomePais = getCountryName(item.alpha2);
      const country = {
        sigla: item.alpha2,
        name: nomePais
      };
      countriesComplete.push(country);
    });
  }

  let c_PaisVenda = ""
  countriesComplete.map((item: any, index: number) => (                                        
    c_PaisVenda += "(" + item.sigla + ") " + item.name + ", "                                  
  ))

  let c_AtualizadoEm = ""
  if (!!data.VBG && !!data.VBG.dateUpdated) {
    c_AtualizadoEm = data.VBG.dateUpdated;
  }

  let c_CodigoErro = "";
  if (!!data.VBG && !!data.VBG.validationErrors && data.VBG.validationErrors.length > 0) {
    c_CodigoErro = data.VBG.validationErrors[0].errors[0].errorCode || "";
  }
   
  let c_gtinOrigem = "";
  let c_qtdGtinOrigem = "";
  if (data?.CNP?.product?.childTradeItems?.length > 0) {
    c_gtinOrigem = data.CNP.product.childTradeItems[0]?.gtin || "";
    c_qtdGtinOrigem = data.CNP.product.childTradeItems[0]?.quantityOfNextLowerLevelTradeItem || "";
  }

  let c_NCM = "";
  let c_CEST = "";
  if (data?.CNP?.product?.tradeItemClassification?.additionalTradeItemClassifications?.length > 0) {
    data.CNP.product.tradeItemClassification.additionalTradeItemClassifications.forEach((element: {
      additionalTradeItemClassificationCodeValue: any; 
      additionalTradeItemClassificationSystemCode: string; 
    }) => {
      if (element.additionalTradeItemClassificationSystemCode === "NCM") {
        c_NCM = element.additionalTradeItemClassificationCodeValue;
      }
      if (element.additionalTradeItemClassificationSystemCode === "CEST") {
        c_CEST = element.additionalTradeItemClassificationCodeValue;    
      }
    });
  }

  let c_pesoBruto = "";
  if (data?.CNP?.product?.tradeItemWeight?.grossWeight) {
    c_pesoBruto = data.CNP.product.tradeItemWeight.grossWeight.value + " " + data.CNP.product.tradeItemWeight.grossWeight.measurementUnitCode;
  }

  let pesoBrutoMinimo = '';
  let pesoBrutoMedio = '';
  let pesoBrutoMaximo = '';
  if (data.CNP && data.CNP.product && data.CNP.product.variableTradeItemInformation && data.CNP.product.variableTradeItemInformation.estimatedWeight && data.CNP.product.variableTradeItemInformation.estimatedWeight !== 'Desconhecido') {
    const info = data.CNP.product.variableTradeItemInformation;
    pesoBrutoMinimo = info.variableWeightRangeMinimum + " " + info.measurementUnitCode;
    pesoBrutoMedio = info.estimatedWeight + " " + info.measurementUnitCode;
    pesoBrutoMaximo = info.variableWeightRangeMaximum + " " + info.measurementUnitCode;
  }

  let c_SincronizadoCCG = "";
  if (data?.CNP?.product) {
    c_SincronizadoCCG = data.CNP.product.syncInformationCCG ? "Sim" : "Não";
  }

  let c_InformacoesAdicionais = "";
  if (
    Array.isArray(data?.CNP?.product?.additionalTradeItemIdentifications) &&
    data.CNP.product.additionalTradeItemIdentifications.length > 0
  ) {
    c_InformacoesAdicionais = formatAdditionalTradeItemIdentificationsList(
      data.CNP.product.additionalTradeItemIdentifications
    );
  }

  let c_gs1nomeEmpresa = "";
  if (!!data.VBG && !!data.VBG.gs1Licence && !!data.VBG.gs1Licence.licenseeName) {
    c_gs1nomeEmpresa = data.VBG.gs1Licence.licenseeName;
  }
  
  let c_Website = "";
  if (!!data.VBG && !!data.VBG.gs1Licence && !!data.VBG.gs1Licence.contactPoint) {
    data.VBG.gs1Licence.contactPoint.map((txt: { website: string; }) =>
      c_Website += txt.website + ", "
    );
  }
  
  let c_tipolicensa = "";
  let c_NroGlobalLocalizacao = "";
  let c_gs1licenciante = "";
  let c_gs1status = "";
  
  if (!!data.VBG && !!data.VBG.gs1Licence && !!data.VBG.gs1Licence.licenceType) {
      c_tipolicensa = data.VBG.gs1Licence.licenceType;
  }
  if (!!data.VBG && !!data.VBG.gs1Licence && !!data.VBG.gs1Licence.licenseeGLN) {
    c_NroGlobalLocalizacao = data.VBG.gs1Licence.licenseeGLN;
  }
  if (!!data.VBG && !!data.VBG.gs1Licence && !!data.VBG.gs1Licence.licensingMO && !!data.VBG.gs1Licence.licensingMO.moName) {
    c_gs1licenciante = data.VBG.gs1Licence.licensingMO.moName;
  }
  if (!!data.GEPIR && !!data.GEPIR.status) {
    c_gs1status = data.GEPIR.status;
  }

  let c_Endereco = "";
  if (!!data.VBG && !!data.VBG.gs1Licence && !!data.VBG.gs1Licence.address) {
    if (!!data.VBG.gs1Licence.address.streetAddress) c_Endereco += data.VBG.gs1Licence.address.streetAddress.value;
    if (!!data.VBG.gs1Licence.address.streetAddressLine2) c_Endereco += data.VBG.gs1Licence.address.streetAddressLine2.value;
    if (!!data.VBG.gs1Licence.address.addressLocality) c_Endereco += data.VBG.gs1Licence.address.addressLocality.value;
    if (!!data.VBG.gs1Licence.address.addressRegion) c_Endereco += data.VBG.gs1Licence.address.addressRegion.value;
    if (!!data.VBG.gs1Licence.address.postalCode) c_Endereco += data.VBG.gs1Licence.address.postalCode;
    if (!!data.VBG.gs1Licence.address.countryCode) c_Endereco += data.VBG.gs1Licence.address.countryCode;
  }

  let c_Erro = "";
  
  if (!!data.VBG && !!data.VBG.validationErrors && data.VBG.validationErrors.length > 0 && data.VBG.validationErrors[0].errors && data.VBG.validationErrors[0].errors.length > 0) {
    c_Erro = data.VBG.validationErrors[0].errors[0].message;
  } 
  else if (data.mensagens) {
    if (data.mensagens.geral != null && data.mensagens.geral.length > 0 && data.mensagens.geral[0].erro === true) {
      let tituloErro = (data.mensagens.geral[0].titulo != null && data.mensagens.geral[0].titulo != undefined) ? data.mensagens.geral[0].titulo : "";
      let msgErro = (data.mensagens.geral[0].mensagem != null && data.mensagens.geral[0].mensagem != undefined) ? data.mensagens.geral[0].mensagem : "";
      c_Erro = (msgErro + " " + tituloErro).trim();
    }
    
    if (data.mensagens.vbg != null && data.mensagens.vbg.length > 0 && !c_Erro && data.mensagens.vbg[0].erro === true) {
      let tituloErro = (data.mensagens.vbg[0].titulo != null && data.mensagens.vbg[0].titulo != undefined) ? data.mensagens.vbg[0].titulo : "";
      let msgErro = (data.mensagens.vbg[0].mensagem != null && data.mensagens.vbg[0].mensagem != undefined) ? data.mensagens.vbg[0].mensagem : "";
      c_Erro = (msgErro + " " + tituloErro).trim();
    }
  }

  if (c_GTIN) {
    const validation = validateGtin(c_GTIN);
    if (validation.error && !c_Erro.toLowerCase().includes("dígito verificador")) {
      c_Erro = c_Erro ? `${c_Erro} | ${validation.message}` : validation.message;
    }
  }
  
  let classGlobalCompleta = "";
  if (c_ClassificacaoGlobal || c_ClassificacaoGlobalNome) {
     classGlobalCompleta = (c_ClassificacaoGlobal + (c_ClassificacaoGlobalNome ? " " + c_ClassificacaoGlobalNome : "")).trim();
  }

 const cnpProduct = data?.CNP?.product || data?.cnp?.product;
  const measurements = cnpProduct?.tradeItemMeasurements;
  let c_Altura = "";
  let c_Largura = "";
  let c_Profundidade = "";

  if (measurements) {
    if (measurements.height?.value != null) {
      c_Altura = `${measurements.height.value} ${getFriendlyUnit(measurements.height.measurementUnitCode)}`.trim();
    }
    if (measurements.width?.value != null) {
      c_Largura = `${measurements.width.value} ${getFriendlyUnit(measurements.width.measurementUnitCode)}`.trim();
    }
    if (measurements.depth?.value != null) {
      c_Profundidade = `${measurements.depth.value} ${getFriendlyUnit(measurements.depth.measurementUnitCode)}`.trim();
    }
  }

  let item: any = {
    'GTIN': c_GTIN || "", 
    'Marca': c_Marca || "",
    'Descrição': c_Descricao || "",
    'URL da imagem': c_URLImagem || "",
    'Classificação global': classGlobalCompleta || "",
    'Conteúdo líquido': c_ConteudoLiquido || "",
    'País de venda': c_PaisVenda || "",
    'Atualizado em': c_AtualizadoEm || "",
    'Código de erro': c_CodigoErro || "",
    'GTIN origem': c_gtinOrigem || "",
    'Qtd. GTIN origem': c_qtdGtinOrigem || "",
    'NCM': c_NCM || "",
    'CEST': c_CEST || "",
    'Altura': c_Altura,
    'Largura': c_Largura,
    'Profundidade': c_Profundidade,
    ...(pesoBrutoMinimo && pesoBrutoMedio && pesoBrutoMaximo
      ? {
          'Peso bruto mínimo': pesoBrutoMinimo,
          'Peso bruto médio': pesoBrutoMedio,
          'Peso bruto máximo': pesoBrutoMaximo
        }
      : {
          'Peso bruto': c_pesoBruto || ""
        }
    ),
    'Sincronizado CCG': c_SincronizadoCCG || "",
    'Informações adicionais': c_InformacoesAdicionais || "",
    'Status': c_gs1status || "",
    'Nome da empresa': c_gs1nomeEmpresa || "",
    'Endereco': c_Endereco || "",
    'Site': c_Website || "",
    'Tipo de Prefixo': c_tipolicensa || "",
    'Número Global de Localização (GLN)': c_NroGlobalLocalizacao || "",
    'Organização Membro GS1': c_gs1licenciante || "",
    'Erro': c_Erro || ""    
  }
  
  item = merge(item, grupos)
  return item;
}